import { atom } from 'jotai';

/**
 * Hour-rolling presets — windows ending at "now" with a fixed length. Used
 * by the Asset Timeline tab (capped at 24h) and any other consumer that needs
 * sub-day granularity. Hour rolls are shift-agnostic: shifts only make sense
 * for day-anchored windows.
 */
export const HOUR_PRESETS = ['1h', '6h', '12h', '24h'] as const;
export type HourPreset = (typeof HOUR_PRESETS)[number];

/**
 * Day-range presets for analytics pickers.
 * '2d' = last 2 days (used on Location Analytics only by default).
 * Exported as a runtime tuple so Zod (factory.schemas) can validate against
 * the same source of truth.
 */
export const DAY_PRESETS = [
  ...HOUR_PRESETS,
  'today',
  'yesterday',
  '2d',
  '7d',
  '30d',
  'custom',
] as const;
export type DayPreset = (typeof DAY_PRESETS)[number];

export function isHourPreset(preset: DayPreset): preset is HourPreset {
  return (HOUR_PRESETS as readonly string[]).includes(preset);
}

/**
 * Shift selection for analytics pickers.
 * 'all' = no shift filter applied to the day range. Any other string is a
 * factory-defined shift ID (see factory.settings.weeklyShifts). Hard-coded
 * defaults (`t1` / `t2` / `t3`) are used as fallback when a day has no shifts
 * configured.
 */
export type ShiftKey = 'all' | (string & {});

/**
 * Time range selection for analytics
 */
export interface TimeRangeSelection {
  start: Date;
  end: Date;
  preset?: DayPreset;
}

/**
 * Asset type filter for analytics.
 * 'all' shows all types; any other string is a factory-configured type ID.
 */
export type AssetTypeFilter = string;

/**
 * Metric types for drilldown analysis
 */
export type MetricType =
  | 'totalAssets'
  | 'activeAssets'
  | 'maintenance'
  | 'distance'
  | 'averageSpeed'
  | 'idleTime'
  | 'workingTime'
  | 'avgVelocity'
  | 'movementTime'
  | 'forkTime'
  | 'assetPerformance'
  | 'utilization';

/**
 * Heatmap visualization mode for the Location Analytics page (DEV2-402).
 * 'activity' = aggregated time-bucketed density (default landing view);
 * 'dwell' = stationary time hotspots;
 * 'traffic' = aggregate density (non-directional).
 */
export type LocationHeatmapMode = 'activity' | 'dwell' | 'traffic';

/**
 * Global selected time range for Dashboard + Reports.
 * Default: Today (from start-of-day to now, approximated at atom init).
 */
export const selectedTimeRangeAtom = atom<TimeRangeSelection>(
  ((): TimeRangeSelection => {
    const now = new Date();
    const startOfDay = new Date(now);
    startOfDay.setHours(0, 0, 0, 0);
    return { start: startOfDay, end: now, preset: 'today' };
  })()
);

/**
 * Global selected shift for Dashboard + Reports. Default: all shifts.
 */
export const selectedShiftAtom = atom<ShiftKey>('all');

/**
 * Page-scoped time range atom for /location-analytics.
 * Kept separate so the global picker state on Reports/Dashboard isn't clobbered
 * by the 2-day cap enforced on the Location Analytics picker.
 * Default: today (start-of-day to now, approximated at atom init).
 */
export const locationAnalyticsTimeRangeAtom = atom<TimeRangeSelection>(
  ((): TimeRangeSelection => {
    const now = new Date();
    const startOfDay = new Date(now);
    startOfDay.setHours(0, 0, 0, 0);
    return { start: startOfDay, end: now, preset: 'today' };
  })()
);

/**
 * Page-scoped shift atom for /location-analytics. Default: all shifts.
 */
export const locationAnalyticsShiftAtom = atom<ShiftKey>('all');

/**
 * Asset type filter (default: all assets)
 */
export const assetTypeFilterAtom = atom<AssetTypeFilter>('all');

/**
 * Selected metric for drilldown dialog (null = no dialog open)
 */
export const selectedMetricAtom = atom<MetricType | null>(null);

/**
 * Selected individual assets for Reports analytics filtering.
 * null = pass-through (show all). Set = explicit selection (including empty
 * Set → show nothing). Consumers must null-check.
 */
export const selectedAssetsAtom = atom<Set<string> | null>(null);

/**
 * Page-scoped selected assets for /location-analytics.
 * Kept separate from the Reports atom so the two pages don't share filter
 * state. Reset when the user enters the Asset Timeline tab — that tab has its
 * own multi-select inside ActivityTimelineWidget and shouldn't inherit a
 * page-level filter.
 */
export const locationAnalyticsSelectedAssetsAtom = atom<Set<string> | null>(null);

/**
 * Location analytics heatmap mode (default: activity / aggregated density)
 */
export const locationHeatmapModeAtom = atom<LocationHeatmapMode>('activity');

// Per-mode radius/intensity sliders. Independent state per tab so flipping
// between Activity, Dwell, and Traffic doesn't drag the previous tab's
// settings along. Activity and Dwell preserve the values that were hard-coded
// before the controls were exposed (radiusPixels: 24, intensity: 1.4).
// Traffic uses tighter defaults (5 / 2.0) to surface point-level flow detail.
export const locationHeatmapActivityRadiusAtom = atom<number>(24);
export const locationHeatmapActivityIntensityAtom = atom<number>(1.4);
export const locationHeatmapDwellRadiusAtom = atom<number>(24);
export const locationHeatmapDwellIntensityAtom = atom<number>(1.4);
export const locationHeatmapTrafficRadiusAtom = atom<number>(5);
export const locationHeatmapTrafficIntensityAtom = atom<number>(2.0);
